// My status — one-tap BRB (optional reason chips) / I'm back / Sign off.
import { useState } from "react";
import { toast } from "sonner";
import { Coffee, LogOut, Play } from "lucide-react";
import { useSquad } from "./SquadContext";
import { STATUS } from "./SquadAvatar";
import AvatarUpload from "./AvatarUpload";
import { BRB_REASONS, since } from "./squadUtils";

export default function MyStatusCard() {
  const { me, setStatus, loadSquad } = useSquad();
  const [reason, setReason] = useState(null);
  const st = me?.status || "offline";
  const s = STATUS[st];
  const run = async (status, r) => {
    try { await setStatus(status, r); setReason(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };
  const label = st === "brb" ? (me?.auto ? "Set to BRB while you were away" : `BRB${me?.reason ? ` · ${me.reason}` : ""} for ${since(me?.since)}`)
    : st === "active" ? "You're available for live calls" : "Signed off";
  return (
    <div className="px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]" data-testid="squad-my-status">
      <div className="flex items-center gap-3">
        <AvatarUpload size={40} status={st} onChanged={loadSquad} compact />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">My status</div>
          <div className={`text-[13px] font-medium truncate ${s.text}`} data-testid="squad-my-status-label">{label}</div>
        </div>
      </div>
      {st === "brb" ? (
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={() => run("active")} data-testid="squad-im-back-btn"
            className="flex-1 h-10 rounded-full bg-emerald-500/90 hover:bg-emerald-400 text-[#06121f] font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
            <Play className="w-4 h-4" /> I'm back
          </button>
          <SignOff onClick={() => run("offline")} />
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="squad-brb-reasons">
            {BRB_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReason(reason === r ? null : r)} data-testid={`squad-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${reason === r ? "border-amber-400/60 bg-amber-400/15 text-amber-200" : "border-white/10 text-slate-400 hover:text-white"}`}>{r}</button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => run("brb", reason)} data-testid="squad-brb-btn"
              className="flex-1 h-10 rounded-full bg-amber-400/90 hover:bg-amber-300 text-[#1a1203] font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
              <Coffee className="w-4 h-4" /> BRB
            </button>
            {st !== "active" && (
              <button type="button" onClick={() => run("active")} data-testid="squad-go-active-btn"
                className="h-10 px-4 rounded-full border border-emerald-400/40 text-emerald-200 text-sm hover:bg-emerald-500/10">Go active</button>
            )}
            {st !== "offline" && <SignOff onClick={() => run("offline")} />}
          </div>
        </>
      )}
    </div>
  );
}

function SignOff({ onClick }) {
  return (
    <button type="button" onClick={onClick} title="Sign off for the day" data-testid="squad-signoff-btn"
      className="h-10 w-10 shrink-0 rounded-full border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 grid place-items-center">
      <LogOut className="w-4 h-4" />
    </button>
  );
}
